/**
 * Event in a Box — Slice 1 orchestration ("one-step composition").
 *
 * Implements docs/DESIGN_EVENT_IN_A_BOX.md §3 (provisioning graph), §4
 * (draft-then-launch, one Launch action), E3 (launch ORDER), and ITERATION 4
 * (workflow modes: pre-register vs show-up).
 *
 * This is PURE ORCHESTRATION over shipped, individually-audited primitives —
 * it adds no new backend. Every step below is an existing service call.
 *
 * Failure model (§4): there is NO automatic compensation. Launch runs the
 * steps in dependency order and STOPS at the first failure, returning the
 * per-step log so the operator sees exactly how far it got. Because the
 * riskiest side effect (the invite send) is scheduled LAST, a mid-launch
 * failure leaves an event that is incompletely configured but has NOT
 * emailed anybody — the safe direction. Re-running Launch after fixing the
 * cause resumes from the recorded state (steps carry their own idempotence:
 * provisioning is idempotent per event, and the wizard passes the ids it
 * already created).
 */

import { audienceFreezeReachSuffix } from "./eibAudienceReach";
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";
import type { QueryCondition, QueryGroup } from "../types/list.types";
import {
  provisionEventTicketing,
  type RegistrationMode,
  type AwardKind,
  type ProvisionAwardKind,
  type EscalationEndMode,
} from "./eventTicketing.service";
import { generateSlug } from "./eventLandingPage.service";
import {
  createList,
  refreshListCount,
  createCombinedList,
  waitForListReady,
  getListById,
} from "./lists.service";
import { createCampaign, createFlight } from "./campaigns.service";

/**
 * §5.g audience controls. These MIRROR the server-side gate
 * (`eib_assert_audience_allowed`) so the wizard can ask for the typed
 * confirmation up front — but the server is the authority, and neither value is
 * bypassable from the client.
 */
export const EIB_AUDIENCE_REVIEW_THRESHOLD = 25000;
export const EIB_AUDIENCE_HARD_CEILING = 100000;

/** ITERATION 4: pre-registration is a workflow CHOICE, not a fixed spine. */
export type EibWorkflowMode = "pre_register" | "show_up";

/**
 * MUST match the shipped `AwardKind` union exactly — the provisioner validates
 * it server-side. ("gift", not "physical".)
 */
export type EibAwardKind = AwardKind;

export interface EibAudienceExisting {
  kind: "existing";
  listId: UUID;
}

/**
 * Membership audience scope for a proximity invitation. Replaces the former
 * free-text `contact_type` list (a stale label) with the authoritative
 * `has_active_subscription` (Zuora-backed) signal:
 *   - members               → existing/former/cancelled member (DEFAULT). Compiles to the virtual
 *                             `is_current_or_former_member` field → eib_is_current_or_former_member():
 *                             has_active_subscription=true OR a terminal contact_subscriptions row.
 *                             Excludes prospects + newsletter-only contacts.
 *   - active_members        → has_active_subscription = true (active only)
 *   - active_plus_prospects → (has_active_subscription = true) OR contact_type = 'Prospect'
 *   - everyone              → no membership filter (proximity only)
 */
export type EibProximityScope =
  | "members"
  | "active_members"
  // Former/cancelled members only (#8 win-back). Compiler virtual field →
  // public.eib_is_former_or_cancelled_member(c.id) (NOT active AND terminal sub).
  // Used as wave B when the "members" audience auto-splits current vs win-back.
  | "former_cancelled_members"
  | "active_plus_prospects"
  | "everyone"
  // Operator-facing multi-select of atomic contact types (current / former /
  // prospect). The emitted membership filter is the OR-union of the selected
  // `contactTypes` (see proximityCriteria). This is the ONLY scope the wizard's
  // "People near the event" audience now produces; the presets above remain in
  // the type solely for the #8 dual-wave machinery (never emitted by the UI).
  | "contact_types";

/**
 * Atomic contact types the operator can combine in the proximity multi-select.
 * Each maps to exactly one whitelisted `build_strict_segment_sql` field:
 *   - current  → has_active_subscription = true       (active Zuora subscription)
 *   - former   → is_former_or_cancelled_member = true (virtual compiler field)
 *   - prospect → contact_type = 'Prospect'
 */
export type EibContactType = "current" | "former" | "prospect";

export interface EibAudienceProximity {
  kind: "proximity";
  /** Membership scope; determines the membership condition emitted alongside
   *  the proximity filter (see EibProximityScope). For the operator-facing
   *  multi-select this is always "contact_types" and the actual filter comes
   *  from `contactTypes`. */
  scope: EibProximityScope;
  /** Selected atomic contact types when scope === "contact_types". The emitted
   *  membership filter is the OR-union of these. Ignored for the preset scopes. */
  contactTypes?: EibContactType[];
  radiusMiles: number;
  /** Anchor: the EVENT's own geocoded coordinates (preferred — the event carries its own
   *  latitude/longitude from its address, independent of whether its org/location is geocoded),
   *  the event's organization (legacy fallback), or an explicit ZIP. The "event venue" UI choice
   *  emits an `event` anchor whenever the event is geocoded (see EventInABoxWizard). */
  anchor:
    | { type: "event"; eventId: UUID }
    | { type: "organization"; organizationId: UUID }
    | { type: "zip"; zip: string };
}

export type EibAudience = EibAudienceExisting | EibAudienceProximity;

export interface EibPlan {
  eventId: UUID;
  eventTitle: string;
  /** Event start, used for scheduling and (Slice 2) day-of reminders. */
  eventStartAt: string;
  /** Event type (e.g. 'gun_show', 'range_night'). Display-only: drives the
   *  wizard's fixed-cadence summary via describeTypeCadence. NOT part of the
   *  canonical idempotency hash (the server derives type from the event). */
  eventType?: string | null;
  /** #8 win-back: which audience this wave targets. 'current' (default) uses the
   *  shipped cadence; 'former_cancelled' uses the win-back cadence + invite
   *  resend. Read server-side by eib_apply_type_cadence via launch.plan. */
  audienceKind?: "current" | "former_cancelled";
  mode: EibWorkflowMode;

  // --- Ticketing + reward (all modes) ---
  // awardKind='none' → ticket-only (no upgrade reward): $0 admission ticket, no
  // reward offer, no auto-escalation; operators upgrade manually on the event page.
  awardKind: ProvisionAwardKind;
  awardName: string | null;
  awardValue: number | null;
  escalationEndMode?: EscalationEndMode;
  escalationEndAt?: string | null;
  /** O1 (iter-4): guest award is PER-EVENT OPT-IN, never a mode default. */
  guestAward: null | {
    kind: EibAwardKind;
    name: string;
    value: number | null;
  };

  // --- Registration (pre_register mode only) ---
  registrationMode?: RegistrationMode;
  openIssuanceCap?: number | null;

  // --- Audience (all modes) ---
  audience: EibAudience;

  // --- Invite flight (all modes) ---
  flightSubject: string;
  /**
   * Code-audit HIGH: the invitation design is NO LONGER operator-typed. The
   * server resolves the blueprint for (mode, guestAward) via eib_resolve_blueprint
   * so an invite can never promise an award the event won't issue (Seam C4 / O1).
   */
  /** ISO datetime to send the invitation. */
  flightSendAt: string;

  /**
   * Slice 2 (reminders) — days before the event to send an invitation reminder,
   * each ∈ {0,1,2} (0 = day-of, 08:00 event-local). The launch stores these on
   * `event_in_a_box_launches.reminder_offsets`; at commit the server creates one
   * sibling reminder flight per offset (fail-closed on a missing reminder
   * blueprint; offsets already past their 4h window are born skipped). The
   * wizard input + begin-RPC write are wired in step (i); until then this stays
   * undefined and no reminders are created.
   */
  reminderOffsets?: number[];

  /**
   * Code-audit BLOCKER: durable launch identity. ONE key per wizard attempt —
   * a retry with the same key reconciles to the same launch (and its already
   * created campaign/flight) instead of scheduling a SECOND mass invitation.
   */
  idempotencyKey: string;
  /** Seam C3: required for show_up — there is no pre-registration signal. */
  expectedCapacity?: number | null;
  /** Show-up only: the operator's explicit check-in acknowledgement. */
  checkinAcknowledged?: boolean;
  /** Typed exact audience size, required above the review threshold. */
  typedAudienceCount?: number | null;
  /** Operator explicitly wants an ADDITIONAL wave for an already-launched event. */
  allowAdditionalWave?: boolean;

  userId: UUID;
}

export type EibStepKey =
  | "launch_record"
  | "blueprint"
  | "ticketing"
  | "registration_mode"
  | "landing_page"
  | "audience"
  | "audience_freeze"
  | "campaign"
  | "flight"
  | "schedule";

export interface EibStepResult {
  key: EibStepKey;
  label: string;
  status: "ok" | "warning" | "skipped" | "failed";
  detail?: string;
  ids?: Record<string, string | null>;
}

/**
 * How much of a frozen EIB audience will actually be mailed, and why the rest will not.
 * Backed by eib_audience_quality_preview_list (migration 2026-09-24-008). A LOW estimate: since
 * migration 394 never-validated addresses are checked before sending rather than dropped, so recent
 * large launches sent about 10-27% more than `will_reach_estimate`. The buckets overlap; operator wording
 * lives in ./eibAudienceReach.ts, which splits them into exclusive groups.
 */
export interface EibAudienceQuality {
  available: boolean;
  reason?: string;
  frozen_total?: number;
  will_reach_estimate?: number;
  excluded_total?: number;
  excluded?: {
    no_email: number;
    suppressed: number;
    invalid: number;
    never_evaluated: number;
  };
  /**
   * Non-empty addresses with no NeverBounce verdict (= excluded.never_evaluated). OVERLAPS
   * `excluded.suppressed`: a do-not-email address stays unsendable even once validated, so this is
   * NOT "recoverable only by validation".
   */
  recoverable_if_validated?: number;
  estimate_note?: string;
}

/**
 * Non-fatal by contract: a launch must never fail because a WARNING could not be computed.
 * Callers treat null as "no estimate available" and carry on.
 */
export async function getEibAudienceQuality(
  listId: string,
): Promise<EibAudienceQuality | null> {
  try {
    const { data, error } = await supabase.rpc(
      "eib_audience_quality_preview_list",
      { p_list_id: listId },
    );
    if (error) {
      return null;
    }
    return data as EibAudienceQuality;
  } catch {
    return null;
  }
}

export interface EibLaunchResult {
  ok: boolean;
  steps: EibStepResult[];
  campaignId?: UUID;
  flightId?: UUID;
  listId?: UUID;
  landingPageId?: UUID;
  /**
   * Size of the FROZEN audience.
   *
   * ⚠️ This is NOT how many people receive the invitation. Measured on PROD 2026-09-24: across
   * the six most recent sent EIB launches, 16,795 frozen contacts never became recipients
   * (22.6%-42.9% per event), 76.8% of them because their address had never been
   * NeverBounce-evaluated and the snapshot gate drops unevaluated addresses. Use
   * `audienceQuality.will_reach_estimate` for the validated-and-deliverable count (a low estimate).
   */
  audienceCount?: number | null;
  /** Estimate of what the frozen audience will actually reach, and why the rest will not. */
  audienceQuality?: EibAudienceQuality | null;
  /** Set when ok=false — the step that stopped the launch. */
  failedAt?: EibStepKey;
  /**
   * #8 auto-dual-wave only. True when wave A (current members) DID commit but
   * wave B (former/cancelled) failed. The operator must be told "current
   * invitations ARE scheduled" — NOT "nothing sent" — so they retry ONLY the
   * former wave (same key, idempotent) instead of re-launching wave A via a
   * fresh key. Accurate reporting is what prevents the duplicate wave-A blast.
   */
  partialSuccess?: boolean;
  /**
   * Set alongside partialSuccess: wave B's OWN failure marker + frozen audience
   * size. The wizard needs wave B's specifics (not the combined result) to react
   * truthfully to a wave-B server decision — e.g. surface an audience-size
   * confirmation for the FORMER wave only while wave A stays scheduled.
   */
  partialWaveB?: { detail: string; audienceCount: number | null };
}

/**
 * Seam C4: the invitation blueprint differs by mode and MUST NOT be mixed.
 * Mode A links to the registration page; Mode C says "just show up" and has
 * no registration CTA. O1: the guest-award promise is bound to the toggle,
 * so copy can never advertise an award the event will not issue.
 */
export function describeBlueprint(
  mode: EibWorkflowMode,
  hasGuestAward: boolean,
  hasMemberReward: boolean = true,
): string {
  // Ticket-only (no upgrade reward): the invitation promises a QR admission
  // ticket, not a dollar reward — the same templates, with member_award_enabled
  // = false. Staff may upgrade the ticket on-site.
  if (!hasMemberReward) {
    return mode === "show_up"
      ? "Show-up invitation — venue/time details, no registration step; invites people to attend and self check-in for a QR ticket (no upgrade reward — staff may upgrade it on-site)."
      : "Pre-registration invitation — links to the registration page; registering issues a QR ticket (no upgrade reward — staff may upgrade it on-site).";
  }
  if (mode === "show_up") {
    return hasGuestAward
      ? "Show-up invitation — venue/time details, no registration step; states that members AND their guests can claim an award at the door."
      : "Show-up invitation — venue/time details, no registration step; states that MEMBERS can claim an award at the door (guests welcome, admission only).";
  }
  return hasGuestAward
    ? "Pre-registration invitation — links to the registration page; states that members and their guests can claim an award."
    : "Pre-registration invitation — links to the registration page; states that MEMBERS can claim an award (guests welcome, admission only).";
}

/** Human-readable label for a reminder offset (days before the event). */
function reminderOffsetLabel(offset: number): string {
  return offset === 0
    ? "day of (8am)"
    : offset === 1
      ? "1 day before"
      : offset === 2
        ? "2 days before"
        : `${offset} days before`;
}

/** Describe the chosen invitation-reminder offsets for the preview summary. */
export function describeReminderOffsets(offsets: number[] | undefined): string {
  if (!offsets || offsets.length === 0) {
    return "none";
  }
  return [...offsets]
    .sort((a, b) => b - a)
    .map(reminderOffsetLabel)
    .join(", ");
}

/** Human-readable plan summary for the PREVIEW step (§3 preview requirement). */
/**
 * Display-only mirror of the server cadence (eib_cadence_for_event_type,
 * migration 2026-08-19-006). The SERVER is authoritative for actual scheduling
 * (eib_apply_type_cadence derives the invite send time + records the reminder
 * offsets); this only drives the wizard's read-only cadence summary. Keep the
 * two in sync. Returns null for event types with no fixed cadence (operator
 * picks the timing manually, unchanged).
 */
export function describeTypeCadence(
  eventType: string | null | undefined,
): { inviteOffsetDays: number; reminderOffsets: number[] } | null {
  switch ((eventType ?? "").trim().toLowerCase()) {
    case "gun_show":
      return { inviteOffsetDays: 4, reminderOffsets: [2] };
    case "range_night":
      return { inviteOffsetDays: 7, reminderOffsets: [4] };
    default:
      return null;
  }
}

/** "the day of the event" / "1 day before" / "N days before" — all at 8am event-local. */
export function cadenceOffsetLabel(offsetDays: number): string {
  if (offsetDays === 0) {
    return "the day of the event";
  }
  if (offsetDays === 1) {
    return "1 day before";
  }
  return `${offsetDays} days before`;
}

export interface EibAppliedCadence {
  applied: boolean;
  send_at?: string | null;
  invite_offset_days?: number;
  reminder_offsets?: number[];
  resend_offset_days?: number | null;
  cadence_window_missed?: boolean;
  original_send_at?: string | null;
  fallback_delay_minutes?: number | null;
}

/** Operator-facing explanation for a non-fatal fixed-cadence adjustment. */
export function describeCadenceWindowAdjustment(
  cadence: EibAppliedCadence,
): string | null {
  if (cadence.cadence_window_missed !== true) {
    return null;
  }
  const offset = Number.isInteger(cadence.invite_offset_days)
    ? `${cadence.invite_offset_days}-day`
    : "standard";
  const original = cadence.original_send_at
    ? new Date(cadence.original_send_at).toLocaleString()
    : "the planned cadence time";
  return (
    `The ${offset} invitation window had already passed (${original}). ` +
    `The primary invitation was moved to the next 8:00 AM in the event's timezone. ` +
    `Reminder or resend steps whose times already passed will be skipped.`
  );
}

export function summarizePlan(
  plan: EibPlan,
  audience: EibAudienceEstimate | null,
): string[] {
  const audienceCount = audience
    ? `${audience.count.toLocaleString()}${audience.capped ? "+" : ""}`
    : null;
  const lines: string[] = [];
  lines.push(
    plan.mode === "show_up"
      ? "Workflow: SHOW UP — no pre-registration. Invitees simply attend; members self-register at the venue and get their QR/award there."
      : "Workflow: PRE-REGISTER — invitees register in advance and their ticket is issued at registration.",
  );
  const ticketOnly = plan.awardKind === "none";
  lines.push(
    ticketOnly
      ? "Reward: TICKET ONLY — a $0 admission ticket, no automatic upgrade reward. Staff can upgrade it manually on the event page (+$20)."
      : `Award: ${plan.awardName ?? ""}${
          plan.awardValue !== null ? ` ($${plan.awardValue})` : ""
        } (${plan.awardKind}).`,
  );
  if (!ticketOnly) {
    lines.push(
      plan.guestAward
        ? `Guests: ALSO awarded — ${plan.guestAward.name}${
            plan.guestAward.value !== null ? ` ($${plan.guestAward.value})` : ""
          }.`
        : "Guests: admission only, no award (member-only award).",
    );
  }
  if (plan.mode === "show_up") {
    lines.push(
      "Public self check-in will be OPENED for this event. It is reachable only on the event's own local day.",
    );
  } else {
    lines.push(
      `Registration: ${
        plan.registrationMode === "open"
          ? `OPEN${plan.openIssuanceCap ? ` (cap ${plan.openIssuanceCap})` : ""}`
          : "INVITE-GATED"
      }.`,
    );
  }
  lines.push(
    plan.audience.kind === "existing"
      ? `Audience: existing segment${audienceCount !== null ? ` — ${audienceCount} contacts` : ""}.`
      : `Audience: within ${plan.audience.radiusMiles} miles of ${
          plan.audience.anchor.type === "zip"
            ? `ZIP ${plan.audience.anchor.zip}`
            : "the event's organization"
        }, ${describeProximityScope(plan.audience)}${
          audienceCount !== null ? ` — ${audienceCount} contacts` : ""
        }.`,
  );
  const cadence = describeTypeCadence(plan.eventType);
  if (cadence) {
    // Fixed-cadence type: the server owns the timing, so show it (not the
    // operator's — unused — manual send time / reminder toggles).
    lines.push(
      `Invitation: "${plan.flightSubject}" sending automatically ${cadenceOffsetLabel(
        cadence.inviteOffsetDays,
      )}, at 8:00 AM event-local.`,
    );
    lines.push(
      describeBlueprint(plan.mode, plan.guestAward !== null, !ticketOnly),
    );
    lines.push(
      cadence.reminderOffsets.length
        ? `Reminders: ${cadence.reminderOffsets
            .map(cadenceOffsetLabel)
            .join(", ")}, at 8:00 AM event-local.`
        : "Reminders: none.",
    );
  } else {
    lines.push(
      `Invitation: "${plan.flightSubject}" sending ${new Date(plan.flightSendAt).toLocaleString()}.`,
    );
    lines.push(
      describeBlueprint(plan.mode, plan.guestAward !== null, !ticketOnly),
    );
    lines.push(`Reminders: ${describeReminderOffsets(plan.reminderOffsets)}.`);
  }
  return lines;
}

/**
 * Render ANY thrown value as operator-readable text. Supabase/PostgREST reject
 * with a plain object (PostgrestError: {message, details, hint, code}), not an
 * Error instance — String(e) on those yields "[object Object]", which tells an
 * operator nothing about why their launch stopped.
 */
export function errorText(e: unknown): string {
  if (e instanceof Error) {
    return e.message;
  }
  if (e && typeof e === "object") {
    const o = e as {
      message?: unknown;
      details?: unknown;
      hint?: unknown;
      code?: unknown;
    };
    const parts = [o.message, o.details, o.hint].filter(
      (x): x is string => typeof x === "string" && x.trim() !== "",
    );
    if (parts.length > 0) {
      return typeof o.code === "string" && o.code
        ? `${parts.join(" — ")} (${o.code})`
        : parts.join(" — ");
    }
    try {
      return JSON.stringify(e);
    } catch {
      return "Unknown error";
    }
  }
  return String(e);
}

/** Canonical (stable) order for atomic contact types, so the emitted OR-union
 *  and its description are deterministic regardless of the order the operator
 *  ticked the boxes. Also dedupes. */
const EIB_CONTACT_TYPE_ORDER: readonly EibContactType[] = [
  "current",
  "former",
  "prospect",
] as const;

/** The single whitelisted segment condition each atomic contact type compiles
 *  to. Every field here is a valid `build_strict_segment_sql` field (verified
 *  against live QA). Booleans MUST be real booleans — the strict compiler
 *  rejects a stringified "true". */
const EIB_CONTACT_TYPE_CONDITION: Record<
  EibContactType,
  QueryCondition | QueryGroup
> = {
  current: {
    field: "has_active_subscription",
    operator: "equals",
    value: true,
  },
  former: {
    field: "is_former_or_cancelled_member",
    operator: "equals",
    value: true,
  },
  // "Prospect" = labeled Prospect AND genuinely never a member. Without the
  // exclusion, a contact labeled Prospect who is ALSO a current/former member
  // would match — contradicting the "not a member" intent (PROD has such
  // dual-labeled rows). `is_current_or_former_member` is the virtual compiler
  // field (has_active_subscription = true OR a terminal subscription); requiring
  // it be false excludes both current and former/cancelled members. The value
  // MUST be a real boolean — the strict compiler rejects a stringified "false".
  prospect: {
    operator: "AND",
    conditions: [
      { field: "contact_type", operator: "equals", value: "Prospect" },
      {
        field: "is_current_or_former_member",
        operator: "equals",
        value: false,
      },
    ],
  },
};

const EIB_CONTACT_TYPE_LABEL: Record<EibContactType, string> = {
  current: "current members",
  former: "former members",
  prospect: "prospects",
};

/** Selected atomic types in canonical order, deduped. */
function orderedContactTypes(
  types: EibContactType[] | undefined,
): EibContactType[] {
  return EIB_CONTACT_TYPE_ORDER.filter((t) => (types ?? []).includes(t));
}

/** Operator-readable label for a proximity audience. */
export function describeProximityScope(a: EibAudienceProximity): string {
  if (a.scope === "contact_types") {
    const selected = orderedContactTypes(a.contactTypes);
    // Fail-closed narration: an empty selection is treated as current members
    // (mirrors proximityCriteria), never "everyone nearby".
    if (selected.length === 0) {
      return EIB_CONTACT_TYPE_LABEL.current;
    }
    return selected.map((t) => EIB_CONTACT_TYPE_LABEL[t]).join(" + ");
  }
  switch (a.scope) {
    case "members":
      return "members (current, former, or cancelled)";
    case "active_members":
      return "active members only";
    case "former_cancelled_members":
      return "former / cancelled members only";
    case "active_plus_prospects":
      return "active members + prospects";
    case "everyone":
      return "everyone nearby (no membership filter)";
    default:
      // Defensive: a stale/hand-crafted plan with an unknown scope FAILS CLOSED to
      // active members in proximityCriteria (never falls open to everyone), so
      // describe it the same way rather than returning undefined.
      return "active members only";
  }
}

/** Build the dynamic-segment criteria for a proximity audience (§5.a).
 *  Exported for unit tests that assert the exact emitted criteria per scope. */
export function proximityCriteria(a: EibAudienceProximity): QueryGroup {
  // The segment compiler expects the `proximity` FIELD with a tuple value
  // [anchor, radiusMiles] — NOT an object, and NOT the `location` field. A
  // wrong shape yields an empty or match-all audience, so this mirrors
  // ProximityValueInput exactly.
  const anchor =
    a.anchor.type === "organization"
      ? a.anchor.organizationId
      : a.anchor.type === "event"
        ? a.anchor.eventId
        : a.anchor.zip;
  const proximityCondition: QueryCondition = {
    field: "proximity",
    // The EVENT carries its own geocoded coordinates (from its address), so an event anchor
    // compiles to within_radius_of_event — the backend branch that reads events.latitude/longitude.
    // This works even when the event's organization/location is not geocoded (the original bug).
    operator:
      a.anchor.type === "organization"
        ? "within_radius_of_org"
        : a.anchor.type === "event"
          ? "within_radius_of_event"
          : "within_radius_of_zip",
    value: [anchor, a.radiusMiles] as [string, number],
  };

  // Membership condition is determined by the explicit scope. The authoritative
  // active-membership signal is `has_active_subscription` (Zuora-backed boolean,
  // whitelisted as a `bool` field in build_strict_segment_sql → compiles to
  // `c.has_active_subscription = true`). The value MUST be a real boolean — the
  // strict compiler rejects a stringified "true".
  const conditions: (QueryCondition | QueryGroup)[] = [];
  if (a.scope === "contact_types") {
    // Operator multi-select: membership filter is the OR-union of the selected
    // atomic contact types, in canonical order (deterministic identity). This is
    // the ONLY scope the wizard emits.
    const selected = orderedContactTypes(a.contactTypes);
    if (selected.length === 0) {
      // FAIL CLOSED: an empty multi-select must NEVER emit a proximity-only
      // (match-all-nearby) audience. Restrict to current members — the same
      // safe default the UI ships with — never "everyone".
      conditions.push(EIB_CONTACT_TYPE_CONDITION.current);
    } else {
      // OR-of-one is intentional and fine for a single selection. Nested OR is
      // supported by build_strict_segment_sql (recurses + parens under parent AND).
      conditions.push({
        operator: "OR",
        conditions: selected.map((t) => EIB_CONTACT_TYPE_CONDITION[t]),
      });
    }
  } else if (a.scope === "members") {
    // Existing/former/cancelled member (the default). Virtual compiler field →
    // public.eib_is_current_or_former_member(c.id) (has_active_subscription = true OR a terminal
    // contact_subscriptions row). Applied uniformly by build_strict_segment_sql at every evaluation
    // point (estimate, dynamic-list count, snapshot freeze) so the preview and the frozen audience
    // cannot disagree. Excludes prospects + newsletter-only contacts.
    conditions.push({
      field: "is_current_or_former_member",
      operator: "equals",
      value: true,
    });
  } else if (a.scope === "former_cancelled_members") {
    // #8 win-back wave: FORMER/CANCELLED members only (excludes current actives +
    // prospects). Virtual compiler field → public.eib_is_former_or_cancelled_member(c.id).
    conditions.push({
      field: "is_former_or_cancelled_member",
      operator: "equals",
      value: true,
    });
  } else if (a.scope === "active_plus_prospects") {
    // Nested OR group — supported by build_strict_segment_sql, which recurses
    // into any condition carrying its own `conditions` array and wraps it in
    // parens joined at the parent AND.
    conditions.push({
      operator: "OR",
      conditions: [
        { field: "has_active_subscription", operator: "equals", value: true },
        { field: "contact_type", operator: "equals", value: "Prospect" },
      ],
    });
  } else if (a.scope === "everyone") {
    // Explicit opt-in to NO membership filter (open/guest events) → proximity only.
  } else {
    // "active_members" AND any missing/malformed scope → FAIL CLOSED to active
    // members. This is a mass-send path; an unknown scope must never fall open to
    // "everyone nearby".
    conditions.push({
      field: "has_active_subscription",
      operator: "equals",
      value: true,
    });
  }
  conditions.push(proximityCondition);

  return { operator: "AND", conditions };
}

/**
 * Canonical plan schema version, written as `plan.v`. The positional `plan`
 * array below is a FIXED contract for this version; rehydration
 * (getLatestEibLaunchForRehydrate) refuses any other version rather than
 * mis-reading a reordered/legacy/future array as the current schema. Bump this
 * ONLY together with a matching reader.
 */
export const EIB_CANONICAL_PLAN_VERSION = 1;

/**
 * Number of positional slots `canonicalPlan` emits (indices 0..14 — see the
 * array literal below). The rehydrator requires this EXACT length before it
 * maps any slot back to a named field; a length mismatch means the stored array
 * is not the v1 shape and is treated as unusable.
 */
export const EIB_CANONICAL_PLAN_LENGTH = 15;

/**
 * The DECISION-BEARING parts of a plan, in a stable order.
 *
 * The server stores this and hashes it (jsonb text is normalized), so a retry
 * under the same key can be proven to be the SAME launch. The digest is
 * deliberately NOT computed here: a client-supplied hash proves nothing,
 * because it can be replayed unchanged while the inputs move.
 *
 * POSITIONAL CONTRACT (v = EIB_CANONICAL_PLAN_VERSION, length =
 * EIB_CANONICAL_PLAN_LENGTH): the index of every slot below is load-bearing —
 * getLatestEibLaunchForRehydrate reads it back by index. Never reorder, insert,
 * or drop a slot without bumping EIB_CANONICAL_PLAN_VERSION and updating both
 * the reader and EIB_CANONICAL_PLAN_LENGTH.
 */
export function canonicalPlan(plan: EibPlan): Record<string, unknown> {
  const canonical = [
    plan.eventId,
    plan.mode,
    plan.awardKind,
    plan.awardName,
    plan.awardValue,
    plan.guestAward
      ? [plan.guestAward.kind, plan.guestAward.name, plan.guestAward.value]
      : null,
    plan.escalationEndMode ?? null,
    plan.escalationEndAt ?? null,
    plan.registrationMode ?? null,
    plan.openIssuanceCap ?? null,
    plan.expectedCapacity ?? null,
    plan.audience.kind === "existing"
      ? ["existing", plan.audience.listId]
      : [
          "proximity",
          plan.audience.scope,
          // #3: the contact-type selection IS part of the audience identity, so
          // it MUST be in the hash. Without it, a failed launch re-launched with
          // a CHANGED selection (same idempotency key) would not trip
          // eib_begin_launch's EIB_PLAN_CHANGED guard and would silently reuse the
          // stale frozen snapshot from the prior selection → wrong audience.
          // Canonical order keeps the hash stable regardless of click order.
          plan.audience.scope === "contact_types"
            ? orderedContactTypes(plan.audience.contactTypes)
            : null,
          plan.audience.radiusMiles,
          plan.audience.anchor.type === "zip"
            ? ["zip", plan.audience.anchor.zip]
            : plan.audience.anchor.type === "event"
              ? ["event", plan.audience.anchor.eventId]
              : ["organization", plan.audience.anchor.organizationId],
        ],
    plan.flightSubject,
    plan.flightSendAt,
    plan.checkinAcknowledged ?? false,
  ] as unknown[];
  // audienceKind is exposed as a TOP-LEVEL key (not just inside the positional
  // array) so the server can read it directly — eib_apply_type_cadence does
  // `launch.plan->>'audienceKind'` to pick the per-audience cadence (#8). It is
  // still part of the hashed plan, so the current + former/cancelled waves are
  // distinct launches.
  return {
    v: EIB_CANONICAL_PLAN_VERSION,
    plan: canonical,
    audienceKind: plan.audienceKind ?? "current",
  };
}

/**
 * FIX 3 — wizard rehydration. Fields recoverable from a PRIOR launch attempt so
 * reopening an event whose EIB configuration was started (committed, in-flight,
 * or failed) restores what was entered instead of starting blank. Every string
 * value is already coerced to the wizard's input-state shape (numbers → the
 * string the text inputs hold); `null` means "not present, keep the blank
 * default". LIMITATION: a pure never-launched draft persists NO EIB-specific
 * state (buildPlan lives only in local component state until Launch writes it
 * to event_in_a_box_launches.plan), so there is nothing to rehydrate for it —
 * full per-step draft persistence is intentionally out of scope.
 */
export interface EibLaunchRehydration {
  mode: EibWorkflowMode | null;
  /** awardKind 'none' ⇒ ticket-only ⇒ rewardEnabled=false; any real kind ⇒ true. */
  rewardEnabled: boolean | null;
  awardKind: EibAwardKind | null;
  awardName: string | null;
  awardValue: string | null;
  guestAwardOn: boolean;
  guestAwardKind: EibAwardKind | null;
  guestAwardName: string | null;
  guestAwardValue: string | null;
  registrationMode: RegistrationMode | null;
  openCap: string | null;
  expectedCapacity: string | null;
  checkinAck: boolean | null;
  audienceKind: "existing" | "proximity" | null;
  existingListId: string | null;
  contactTypes: EibContactType[] | null;
  radiusMiles: string | null;
  anchorType: "organization" | "zip" | null;
  anchorZip: string | null;
  flightSubject: string | null;
  /** datetime-local value (YYYY-MM-DDTHH:mm, local wall-clock) — the plan stores
   *  an ISO/UTC string; this is converted back for the <input type=datetime-local>. */
  flightSendAt: string | null;
  reminderOffsets: number[] | null;
}

/** ISO/UTC (as stored in the plan) → datetime-local input value (local wall-clock). */
function isoToLocalInput(iso: unknown): string | null {
  if (typeof iso !== "string" || iso === "") {
    return null;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

const numToInput = (n: unknown): string | null =>
  typeof n === "number" && Number.isFinite(n) ? String(n) : null;

/**
 * Result of a rehydration lookup. The three outcomes are DISTINCT on purpose so
 * the wizard can react correctly to each:
 *  - `ok`         — a usable prior plan was found and parsed (`data`).
 *  - `not_found`  — the event has no launch row (a legitimate never-launched
 *                   draft): the wizard keeps its blank defaults and stays
 *                   launchable.
 *  - `unusable`   — a launch row exists but its plan is NOT the current
 *                   positional schema (wrong/absent `v`, wrong length/shape).
 *                   We refuse to guess: parsing a reordered/legacy/future array
 *                   as v1 would silently load the wrong award/capacity/audience.
 *  - `error`      — the query itself failed. This is NOT "no previous launch";
 *                   proceeding with blank defaults could launch a config the
 *                   operator never intended, so the caller must block instead.
 */
export type EibRehydrateResult =
  | { status: "ok"; data: EibLaunchRehydration }
  | { status: "not_found" }
  | { status: "unusable"; reason: string }
  | { status: "error"; message: string };

/**
 * Latest launch attempt for an event, parsed into wizard-input shape for
 * rehydration. Reads the FROZEN canonical `plan` jsonb ({ v, plan: [...], ... },
 * the exact output of canonicalPlan above) plus the sibling `reminder_offsets`
 * column, and maps its POSITIONAL array back to named fields. The index layout
 * MUST stay in lockstep with canonicalPlan — a drift here silently mis-restores,
 * which is exactly why the parse is version- AND length-gated below.
 *
 * Returns a typed EibRehydrateResult (see above) — NOT a bare null — so the
 * caller can tell "no previous launch" (not_found) from "the lookup failed"
 * (error) from "the stored plan is an unrecognized schema" (unusable), and pick
 * a safe UI for each instead of silently starting blank.
 */
export async function getLatestEibLaunchForRehydrate(
  eventId: UUID,
): Promise<EibRehydrateResult> {
  const { data, error } = await supabase
    .from("event_in_a_box_launches")
    // status CHECK is exactly (in_progress|committed|failed); newest wins.
    .select("plan, reminder_offsets, status, created_at")
    .eq("event_id", eventId)
    // A radius expansion's launch is not a wizard setup (its plan is the expansion request);
    // rehydrate only from wizard ("original") launches.
    .eq("launch_kind", "original")
    .in("status", ["in_progress", "failed", "committed"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    // A real query failure — DISTINCT from "no row". Do not let the caller treat
    // this as a blank-but-launchable new configuration.
    return { status: "error", message: error.message ?? "lookup failed" };
  }
  if (!data) {
    return { status: "not_found" };
  }
  const row = data as {
    plan: { v?: unknown; plan?: unknown } | null;
    reminder_offsets: number[] | null;
  };

  // STRICT versioned parse (code-audit HIGH). Only the exact v1 positional shape
  // is trusted; anything else (missing/other `v`, non-array, wrong length) is a
  // schema we cannot read by index, so we bail with `unusable` rather than
  // mis-mapping a reordered/legacy/future array onto the current fields.
  if (!row.plan || typeof row.plan !== "object") {
    return { status: "unusable", reason: "plan envelope missing" };
  }
  if (row.plan.v !== EIB_CANONICAL_PLAN_VERSION) {
    return {
      status: "unusable",
      reason: `unsupported plan version ${String(row.plan.v)}`,
    };
  }
  if (
    !Array.isArray(row.plan.plan) ||
    row.plan.plan.length !== EIB_CANONICAL_PLAN_LENGTH
  ) {
    return {
      status: "unusable",
      reason: `expected a length-${EIB_CANONICAL_PLAN_LENGTH} positional array`,
    };
  }
  const p = row.plan.plan as unknown[];

  // Positional layout — see canonicalPlan():
  //  1 mode · 2 awardKind · 3 awardName · 4 awardValue · 5 guestAward[kind,name,value]|null
  //  8 registrationMode · 9 openIssuanceCap · 10 expectedCapacity
  //  11 audience(["existing",listId] | ["proximity",scope,contactTypes|null,radius,anchor])
  //  12 flightSubject · 13 flightSendAt · 14 checkinAcknowledged
  const awardKindRaw = typeof p[2] === "string" ? (p[2] as string) : null;
  const guest = Array.isArray(p[5])
    ? (p[5] as [unknown, unknown, unknown])
    : null;
  const audience = Array.isArray(p[11]) ? (p[11] as unknown[]) : null;

  let audienceKind: "existing" | "proximity" | null = null;
  let existingListId: string | null = null;
  let contactTypes: EibContactType[] | null = null;
  let radiusMiles: string | null = null;
  let anchorType: "organization" | "zip" | null = null;
  let anchorZip: string | null = null;
  if (audience && audience[0] === "existing") {
    audienceKind = "existing";
    existingListId = typeof audience[1] === "string" ? audience[1] : null;
  } else if (audience && audience[0] === "proximity") {
    audienceKind = "proximity";
    if (Array.isArray(audience[2]) && audience[2].length > 0) {
      contactTypes = audience[2] as EibContactType[];
    }
    radiusMiles = numToInput(audience[3]);
    const anchor = Array.isArray(audience[4])
      ? (audience[4] as [unknown, unknown])
      : null;
    if (anchor && anchor[0] === "zip") {
      anchorType = "zip";
      anchorZip = typeof anchor[1] === "string" ? anchor[1] : null;
    } else if (
      anchor &&
      (anchor[0] === "organization" || anchor[0] === "event")
    ) {
      // Both the legacy org anchor and the (preferred) event anchor map back to the single
      // "event venue" UI choice. The wizard re-derives the concrete anchor (event coords if
      // geocoded, else org) from the freshly-loaded event on the next launch, so we don't thread
      // the id here — avoids racing that fetch.
      anchorType = "organization";
    }
  }

  const rehydration: EibLaunchRehydration = {
    mode: p[1] === "pre_register" || p[1] === "show_up" ? p[1] : null,
    rewardEnabled: awardKindRaw === null ? null : awardKindRaw !== "none",
    awardKind:
      awardKindRaw && awardKindRaw !== "none"
        ? (awardKindRaw as EibAwardKind)
        : null,
    awardName: typeof p[3] === "string" ? p[3] : null,
    awardValue: numToInput(p[4]),
    guestAwardOn: guest !== null,
    guestAwardKind:
      guest && typeof guest[0] === "string" ? (guest[0] as EibAwardKind) : null,
    guestAwardName: guest && typeof guest[1] === "string" ? guest[1] : null,
    guestAwardValue: guest ? numToInput(guest[2]) : null,
    registrationMode:
      p[8] === "invite_gated" || p[8] === "open"
        ? (p[8] as RegistrationMode)
        : null,
    openCap: numToInput(p[9]),
    expectedCapacity: numToInput(p[10]),
    checkinAck: typeof p[14] === "boolean" ? p[14] : null,
    audienceKind,
    existingListId,
    contactTypes,
    radiusMiles,
    anchorType,
    anchorZip,
    flightSubject: typeof p[12] === "string" && p[12] !== "" ? p[12] : null,
    flightSendAt: isoToLocalInput(p[13]),
    reminderOffsets: Array.isArray(row.reminder_offsets)
      ? row.reminder_offsets
      : null,
  };
  return { status: "ok", data: rehydration };
}

/**
 * Have the server verify the blueprint's template against SendGrid and record
 * a short-lived, single-use ATTESTATION bound to this launch.
 *
 * The check cannot live here: a client-side "I looked it up" is not proof to
 * the database, and a long-lived blueprint stamp cannot see an active-version
 * replacement made minutes later. The edge function holds the SendGrid key and
 * writes the attestation with the service role; eib_schedule_and_commit
 * consumes it, so an unattested, expired, or already-used design can never be
 * scheduled.
 */
async function attestSendgridTemplate(
  launchId: string,
  templateId: string,
): Promise<string> {
  const { data, error } = await supabase.functions.invoke(
    "eib-validate-blueprint",
    { body: { launch_id: launchId, template_id: templateId } },
  );
  if (error) {
    // supabase.functions.invoke reports only "non-2xx status code"; the useful
    // part (Template not found / no active version / SendGrid refused) is in the body.
    let status: number | null = null;
    let body: unknown = null;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.text === "function") {
      status = typeof ctx.status === "number" ? ctx.status : null;
      try {
        body = JSON.parse(await ctx.text());
      } catch {
        body = null;
      }
    }
    throw new Error(
      describeAttestationFailure(templateId, status, body, errorText(error)),
    );
  }
  const body = data as {
    success?: boolean;
    error?: string;
    active_version_id?: string;
  } | null;
  if (!body?.success || !body.active_version_id) {
    throw new Error(
      `The invitation design (template ${templateId}) is not usable: ${
        body?.error ?? "no active version in SendGrid"
      }`,
    );
  }
  return body.active_version_id;
}

/**
 * Operator text for a failed design check. When SendGrid itself refused or was unavailable
 * (eib-validate-blueprint answers with a `sendgrid_*` code — 2026-10-16: an IP allowlist made every
 * check 403 for hours) the server's plain sentence is shown as-is: it already says nothing was sent
 * and what to do. Anything else keeps the template-specific wording.
 */
export function describeAttestationFailure(
  templateId: string,
  httpStatus: number | null,
  body: unknown,
  fallback: string,
): string {
  const b =
    body && typeof body === "object"
      ? (body as { error?: unknown; code?: unknown })
      : {};
  const msg = typeof b.error === "string" && b.error.trim() ? b.error : null;
  const code = typeof b.code === "string" ? b.code : null;
  if (msg && code && code.startsWith("sendgrid_")) {
    return `${msg} (checking invitation design ${templateId}${
      httpStatus ? `, HTTP ${httpStatus}` : ""
    })`;
  }
  const detail = msg
    ? `${msg}${httpStatus ? ` (HTTP ${httpStatus})` : ""}`
    : fallback;
  return `The invitation design (template ${templateId}) could not be verified in SendGrid: ${detail}`;
}

/**
 * Steps after which a failed attempt holds something worth adopting (a frozen audience, a campaign, an
 * invitation). Mirrors C_STEPS in eib_find_resumable_launch (migration 2026-10-16-880).
 */
export const EIB_RESUMABLE_STEPS: readonly EibStepKey[] = [
  "audience_freeze",
  "campaign",
  "flight",
  "schedule",
] as const;

/** Server answer from eib_find_resumable_launch. */
export type EibResumeCandidate =
  | {
      resumable: true;
      launchId: string;
      idempotencyKey: string;
      failedAtStep: EibStepKey;
      failedAt: string | null;
      startedAt: string | null;
      campaignId: string | null;
      flightId: string | null;
      snapshotListId: string | null;
      audienceCount: number | null;
    }
  | { resumable: false; reason: string };

/**
 * Can a REOPENED wizard continue this event's latest failed attempt? The server compares the plan hash,
 * the attempt's owner and age, and that its campaign / invitation / frozen audience are still untouched
 * drafts; it returns the attempt's key only when every check passes. Any error is "no" — the safe
 * fallback is a fresh launch, exactly as before.
 */
export async function findResumableEibLaunch(
  plan: EibPlan,
): Promise<EibResumeCandidate> {
  try {
    const { data, error } = await supabase.rpc("eib_find_resumable_launch", {
      p_event_id: plan.eventId,
      p_plan: canonicalPlan(plan),
      p_allow_additional: plan.allowAdditionalWave ?? false,
    });
    if (error || !data || typeof data !== "object") {
      return {
        resumable: false,
        reason: error ? `lookup_failed: ${errorText(error)}` : "lookup_failed",
      };
    }
    const d = data as Record<string, unknown>;
    const str = (v: unknown) => (typeof v === "string" && v ? v : null);
    if (
      d.resumable === true &&
      str(d.idempotency_key) &&
      str(d.launch_id) &&
      EIB_RESUMABLE_STEPS.includes(d.failed_at_step as EibStepKey)
    ) {
      return {
        resumable: true,
        launchId: d.launch_id as string,
        idempotencyKey: d.idempotency_key as string,
        failedAtStep: d.failed_at_step as EibStepKey,
        failedAt: str(d.failed_at),
        startedAt: str(d.started_at),
        campaignId: str(d.campaign_id),
        flightId: str(d.flight_id),
        snapshotListId: str(d.snapshot_list_id),
        audienceCount:
          typeof d.audience_count === "number" ? d.audience_count : null,
      };
    }
    return {
      resumable: false,
      reason: typeof d.reason === "string" ? d.reason : "not_resumable",
    };
  } catch (e) {
    return { resumable: false, reason: `lookup_failed: ${errorText(e)}` };
  }
}

/**
 * Pick the key a wizard launch uses. Unless the operator chose "Start a fresh setup", a resumable
 * earlier attempt's key replaces the wizard's own: eib_begin_launch then takes its existing same-key
 * resume path (plan hash re-checked, fencing token rotated, campaign/invitation/audience adopted).
 */
export async function chooseEibLaunchKey(
  plan: EibPlan,
  opts: { resumeOptOut: boolean },
): Promise<{ plan: EibPlan; resumed: EibResumeCandidate | null }> {
  if (opts.resumeOptOut) {
    return { plan, resumed: null };
  }
  const found = await findResumableEibLaunch(plan);
  if (!found.resumable) {
    return { plan, resumed: found };
  }
  return {
    plan: { ...plan, idempotencyKey: found.idempotencyKey },
    resumed: found,
  };
}

/**
 * After a launch commits: cancel the never-sent draft invitations of earlier FAILED attempts that this
 * commit superseded (eib_retire_superseded_drafts). Housekeeping only — those drafts can never send and
 * the attempts can no longer resume — so a failure here must never fail the launch.
 */
async function retireSupersededEibDrafts(eventId: string): Promise<void> {
  try {
    const { error } = await supabase.rpc("eib_retire_superseded_drafts", {
      p_event_id: eventId,
    });
    if (error) {
      console.warn("EIB: retiring superseded drafts failed", errorText(error));
    }
  } catch (e) {
    console.warn("EIB: retiring superseded drafts failed", errorText(e));
  }
}

/**
 * The invitation flight belonging to THIS launch, by identity.
 *
 * Adoption used to take the oldest flight in the campaign, which relied on an
 * undocumented "one flight per EIB campaign" assumption — a hand-added flight,
 * or a Slice 2 reminder, would have been adopted and scheduled as the
 * invitation. campaign_flights.eib_launch_id is unique and set at creation.
 */
async function getLaunchFlight(launchId: string): Promise<UUID | null> {
  const { data, error } = await supabase
    .from("campaign_flights")
    .select("id")
    .eq("eib_launch_id", launchId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  return ((data as { id?: UUID } | null)?.id ?? null) as UUID | null;
}

/**
 * LAUNCH — executes the provisioning graph in dependency order (E3).
 *
 * Order rationale (E3): everything the invitation depends on must exist and be
 * live BEFORE the invitation is scheduled. The flight schedule is therefore the
 * LAST write; a failure before it means nothing was emailed.
 */
export async function launchEventInABox(
  plan: EibPlan,
  /** Live progress for the operator — freezing waits on an async build. */
  onProgress?: (message: string) => void,
): Promise<EibLaunchResult> {
  const steps: EibStepResult[] = [];
  const result: EibLaunchResult = { ok: false, steps };
  let launchId: string | null = null;
  // Fencing token: proves this client still owns the launch. Every mutating
  // call carries it, so a superseded attempt (one whose slot was released and
  // retaken) cannot write anything — including scheduling.
  let token: string | null = null;

  const fail = async (
    key: EibStepKey,
    label: string,
    e: unknown,
  ): Promise<EibLaunchResult> => {
    steps.push({ key, label, status: "failed", detail: errorText(e) });
    result.failedAt = key;
    result.ok = false;
    // Release the event's single active launch slot immediately, so the
    // operator can correct and retry instead of waiting out the stale sweep.
    if (launchId) {
      await supabase
        .rpc("eib_fail_launch", {
          p_launch_id: launchId,
          p_reason: key,
          p_token: token,
        })
        .then(
          () => undefined,
          () => undefined,
        );
    }
    return result;
  };

  // Every RPC below goes through this: supabase.rpc() RESOLVES with {error}
  // rather than rejecting, so an unchecked call silently continues past a
  // failure — which is how a flight could be scheduled without its id ever
  // reaching the launch record.
  const rpc = async <T>(
    fn: string,
    args: Record<string, unknown>,
  ): Promise<T> => {
    const { data, error } = await supabase.rpc(fn, args);
    if (error) {
      throw error;
    }
    return data as T;
  };

  // 0) DURABLE LAUNCH IDENTITY.
  //
  // The launch record is created FIRST and carries a canonical hash of the
  // plan. A retry with the same key resumes that row (and its already-created
  // artifacts); a retry with a CHANGED plan is refused, because the first
  // attempt's partial setup would never be reversed. Only one launch may be in
  // flight per event at a time, enforced server-side.
  let waveNumber = 1;
  try {
    onProgress?.("Preparing…");
    const begun = await rpc<{
      launch_id: string;
      status: string;
      reused: boolean;
      wave_number: number;
      fencing_token: string;
      flight_id: string | null;
      campaign_id: string | null;
      snapshot_list_id: string | null;
      landing_page_id: string | null;
      audience_count: number | null;
      result: Record<string, unknown> | null;
    }>("eib_begin_launch", {
      p_event_id: plan.eventId,
      p_idempotency_key: plan.idempotencyKey,
      p_workflow_mode: plan.mode,
      p_guest_award_enabled: plan.guestAward !== null,
      p_expected_capacity: plan.expectedCapacity ?? null,
      p_checkin_acknowledged: plan.checkinAcknowledged ?? false,
      // The FULL canonical plan: the server hashes this itself.
      p_plan: canonicalPlan(plan),
      p_allow_additional: plan.allowAdditionalWave ?? false,
    });
    launchId = begun.launch_id;
    token = begun.fencing_token;
    waveNumber = begun.wave_number ?? 1;

    // RECONCILIATION: this key already completed. Report the stored outcome
    // instead of launching again — this is the lost-response case.
    if (begun.status === "committed") {
      steps.push({
        key: "launch_record",
        label:
          "This launch already completed — showing the original result (nothing was sent twice)",
        status: "ok",
        ids: { launch_id: launchId, flight_id: begun.flight_id },
      });
      result.ok = true;
      result.campaignId = (begun.campaign_id ?? undefined) as UUID | undefined;
      result.flightId = (begun.flight_id ?? undefined) as UUID | undefined;
      result.listId = (begun.snapshot_list_id ?? undefined) as UUID | undefined;
      result.landingPageId = (begun.landing_page_id ?? undefined) as
        | UUID
        | undefined;
      result.audienceCount = begun.audience_count ?? null;
      await retireSupersededEibDrafts(plan.eventId);
      return result;
    }
    if (begun.reused) {
      result.flightId = (begun.flight_id ?? undefined) as UUID | undefined;
      result.campaignId = (begun.campaign_id ?? undefined) as UUID | undefined;
      result.listId = (begun.snapshot_list_id ?? undefined) as UUID | undefined;
      result.landingPageId = (begun.landing_page_id ?? undefined) as
        | UUID
        | undefined;
      result.audienceCount = begun.audience_count ?? null;
    }
    steps.push({
      key: "launch_record",
      label: begun.reused
        ? "Resuming the previous attempt"
        : waveNumber > 1
          ? `Launch started (invitation wave ${waveNumber})`
          : "Launch started",
      status: "ok",
      ids: { launch_id: launchId },
    });
  } catch (e) {
    return fail("launch_record", "Starting the launch", e);
  }

  // Slice 2 (reminders) + per-type invitation cadence.
  //
  // For event types with a FIXED cadence (gun_show, range_night) the server owns
  // the timing: eib_apply_type_cadence records the reminder offsets itself
  // (through the same token-guarded setter) and returns the authoritative invite
  // send time (N days before the event at 08:00 event-local). The operator does
  // not pick either — the wizard shows the cadence read-only. For every other
  // type it returns {applied:false} and we persist the operator's chosen offsets
  // exactly as before. eib_schedule_and_commit reads reminder_offsets at commit
  // to create one reminder sibling per offset. '{}' = no reminders (the default).
  let effectiveSendAt = plan.flightSendAt;
  let cadenceWindowWarning: string | null = null;
  try {
    const cadence = await rpc<EibAppliedCadence>("eib_apply_type_cadence", {
      p_launch_id: launchId,
      p_token: token,
    });
    if (cadence?.applied) {
      // Server-derived invite time; reminder offsets already recorded by the RPC.
      if (cadence.send_at) {
        effectiveSendAt = cadence.send_at;
      }
      cadenceWindowWarning = describeCadenceWindowAdjustment(cadence);
    } else {
      await rpc<void>("eib_set_reminder_offsets", {
        p_launch_id: launchId,
        p_offsets: plan.reminderOffsets ?? [],
        p_token: token,
      });
    }
  } catch (e) {
    return fail("launch_record", "Recording the reminder schedule", e);
  }

  const recordArtifact = (
    key: string,
    id: string,
    audienceCount?: number | null,
  ) =>
    rpc<void>("eib_record_launch_artifact", {
      p_launch_id: launchId,
      p_key: key,
      p_id: id,
      p_audience_count: audienceCount ?? null,
      p_token: token,
    });

  // 1) Ticket + ratchet award (+ optional guest award). Idempotent per event.
  //
  // The RESULT matters: on an additional wave the provisioning row is locked,
  // so a newly-requested guest award is NOT applied. Launching anyway would
  // mail an invitation promising an award the door will not issue.
  try {
    onProgress?.("Setting up the ticket and award…");
    const prov = await provisionEventTicketing({
      eventId: plan.eventId,
      awardKind: plan.awardKind,
      awardName: plan.awardName,
      awardValue: plan.awardValue,
      escalationEndMode: plan.escalationEndMode ?? "end_of_day",
      escalationEndAt: plan.escalationEndAt ?? null,
      guestAwardKind: plan.guestAward?.kind ?? null,
      guestAwardName: plan.guestAward?.name ?? null,
      guestAwardValue: plan.guestAward?.value ?? null,
    });
    if (prov.blocked) {
      throw new Error(
        `Ticket provisioning was blocked: ${prov.reason ?? "unknown reason"}`,
      );
    }
    // A LOCKED provisioning row silently keeps the OLD contract. Comparing only
    // the guest dimension was not enough: an additional wave could change the
    // member award's kind, name or value and still invite people to it. This
    // compares the whole persisted contract and refuses any difference.
    await rpc<void>("eib_assert_award_contract", {
      p_event_id: plan.eventId,
      p_award_kind: plan.awardKind,
      p_award_name: plan.awardName,
      p_award_value: plan.awardValue,
      p_guest_requested: plan.guestAward !== null,
      p_guest_name: plan.guestAward?.name ?? null,
      p_guest_value: plan.guestAward?.value ?? null,
      p_guest_kind: plan.guestAward?.kind ?? null,
      p_escalation_end_mode: plan.escalationEndMode ?? "end_of_day",
      p_escalation_end_at: plan.escalationEndAt ?? null,
      // Stored on the FENCED launch so scheduling can revalidate the contract
      // after the audience-freeze window.
      p_launch_id: launchId,
      p_token: token,
    });
    steps.push({
      key: "ticketing",
      label: prov.locked
        ? "Ticket + award already provisioned (locked — existing award kept)"
        : "Ticket + award provisioned",
      status: "ok",
    });
  } catch (e) {
    return fail("ticketing", "Ticket + award provisioning", e);
  }

  // 1b) Resolve the STANDARDIZED invitation design from the PERSISTED award
  //     contract (not from the request). The SendGrid check is deliberately
  //     NOT here — it happens immediately before scheduling, so the attestation
  //     is still fresh when it is consumed.
  let templateId: string;
  let mergeData: Record<string, unknown> = {};
  try {
    const bp = await rpc<{
      template_id: string;
      label: string;
      merge_data: Record<string, unknown>;
    }>("eib_resolve_blueprint_for_event", {
      p_event_id: plan.eventId,
      p_workflow_mode: plan.mode,
      p_requested_guest_award: plan.guestAward !== null,
    });
    templateId = bp.template_id;
    // The business fixed ONE invite template for every workflow and both
    // guest-award settings, so the template can no longer encode the award
    // promise by SELECTION. These merge variables are what the copy must branch
    // on instead — the invitation cannot describe a guest award unless the
    // event actually issues one, because this comes from the persisted contract.
    mergeData = bp.merge_data ?? {};
    steps.push({
      key: "blueprint",
      label: `Invitation design: ${bp.label} (${
        mergeData.guest_award_enabled ? "members + guests" : "members only"
      })`,
      status: "ok",
    });
  } catch (e) {
    return fail("blueprint", "Invitation design", e);
  }

  // 2) Mode-specific settings — ONE fenced server operation.
  //
  // These used to be four unfenced client calls (registration mode, capacity,
  // check-in state, event flags). A superseded client could still apply them
  // after another launch had taken the event's slot, and would only discover it
  // later at a fenced call — leaving those mutations behind. Now they carry the
  // launch identity and token, so a stale attempt changes nothing.
  try {
    await rpc<{ workflow_mode: string }>("eib_apply_workflow_settings", {
      p_launch_id: launchId,
      p_token: token,
      p_registration_mode:
        plan.mode === "pre_register"
          ? (plan.registrationMode ?? "invite_gated")
          : null,
      p_open_issuance_cap: plan.openIssuanceCap ?? null,
    });
    steps.push({
      key: "registration_mode",
      label:
        plan.mode === "pre_register"
          ? `Registration set to ${plan.registrationMode ?? "invite_gated"}; public self check-in CONTAINED`
          : `Capacity set to ${plan.expectedCapacity}; public self check-in OPENED (event's local day only)`,
      status: "ok",
    });
  } catch (e) {
    return fail("registration_mode", "Workflow settings", e);
  }

  // 3) Landing page + registration list — one idempotent transaction, so a
  //    failure can never leave a published page with nowhere to register.
  if (plan.mode === "pre_register") {
    try {
      const page = await rpc<{ landing_page_id: string; created: boolean }>(
        "eib_ensure_landing_page",
        {
          p_event_id: plan.eventId,
          p_slug: generateSlug(plan.eventTitle),
          p_list_name: `${plan.eventTitle} — Registrations`,
          p_launch_id: launchId,
          p_token: token,
        },
      );
      result.landingPageId = page.landing_page_id as UUID;
      await recordArtifact("landing_page_id", page.landing_page_id);
      steps.push({
        key: "landing_page",
        label: page.created
          ? "Registration page created + published"
          : "Registration page already existed — reused and published",
        status: "ok",
        ids: { landing_page_id: page.landing_page_id },
      });
    } catch (e) {
      return fail("landing_page", "Registration page", e);
    }
  } else {
    steps.push({
      key: "landing_page",
      label: "Registration page (not used in show-up mode)",
      status: "skipped",
    });
  }

  // 5) Audience — reuse the frozen snapshot if this retry already built one.
  let listId: UUID;
  let frozenCount: number | null = result.audienceCount ?? null;
  let reusedSnapshot = false;
  if (result.listId) {
    try {
      const existing = await getListById(result.listId);
      if (existing && existing.build_status === "ready") {
        listId = result.listId;
        frozenCount = existing.contact_count ?? frozenCount;
        reusedSnapshot = true;
        steps.push({
          key: "audience",
          label: `Reusing the audience frozen by the previous attempt${
            frozenCount !== null
              ? ` — ${frozenCount.toLocaleString()} contacts`
              : ""
          }`,
          status: "ok",
          ids: { snapshot_list_id: listId },
        });
      }
    } catch {
      // fall through and build a fresh audience
    }
  }

  if (!reusedSnapshot) {
    try {
      if (plan.audience.kind === "existing") {
        listId = plan.audience.listId;
        steps.push({
          key: "audience",
          label: "Audience: existing segment",
          status: "ok",
          ids: { list_id: listId },
        });
      } else {
        const list = await createList(
          {
            name: `${plan.eventTitle} — Invite audience`,
            description: `Auto-created by Event in a Box for "${plan.eventTitle}".`,
            is_dynamic: true,
            query_criteria: proximityCriteria(plan.audience),
          },
          plan.userId,
        );
        listId = list.id;
        await refreshListCount(list.id).catch(() => undefined);
        steps.push({
          key: "audience",
          label: "Audience: proximity segment created",
          status: "ok",
          ids: { list_id: listId },
        });
      }
    } catch (e) {
      return fail("audience", "Audience", e);
    }
  }

  // 5b) E1/§5.a — FREEZE the audience to an event-owned snapshot, then apply
  //     the server-side audience controls before anything can be scheduled.
  try {
    if (!reusedSnapshot) {
      onProgress?.(
        "Freezing the invite audience for this event… large audiences can take several minutes.",
      );
      const snapshotId = await createCombinedList(
        [listId!],
        [],
        `${plan.eventTitle} — Invite snapshot ${new Date().toISOString().slice(0, 10)}`,
      );
      // Interim: waitForListReady defaults to a 5-min cap, which a large proximity
      // audience can exceed (e.g. ~14k members ≈ 6 min) — the client then gave up
      // "still building" and failed the launch even though the build finished fine.
      // Give the freeze 20 min here. TODO(follow-on): replace the fixed cap with a
      // progress-aware wait (keep going while inserted_count advances; fail only on a
      // true stall) so audiences of any size complete. [[todo_eib_audience_freeze_progress_wait]]
      const ready = await waitForListReady(snapshotId, {
        timeoutMs: 20 * 60 * 1000,
      });
      if (ready.build_status === "failed") {
        return fail(
          "audience_freeze",
          "Freezing the audience",
          new Error(
            "The audience snapshot failed to build — nothing was sent.",
          ),
        );
      }
      frozenCount = ready.contact_count ?? null;
      listId = snapshotId;
      await recordArtifact("snapshot_list_id", snapshotId, frozenCount);
    }
    result.listId = listId!;
    result.audienceCount = frozenCount;

    // Audience controls. The server DERIVES the size from the snapshot itself
    // (a count passed from here would be a number the client chose) and RECORDS
    // the authorization on the launch, which the scheduling transaction then
    // requires — so a commit cannot happen without these controls having run.
    const authorized = await rpc<{ audience_count: number }>(
      "eib_authorize_audience",
      {
        p_launch_id: launchId,
        p_token: token,
        p_typed_count: plan.typedAudienceCount ?? null,
      },
    );
    frozenCount = authorized.audience_count;
    result.audienceCount = frozenCount;
    // Attach the reach estimate. Non-fatal: the launch must not fail because a warning could
    // not be computed, so getEibAudienceQuality swallows its own errors and returns null.
    // result.listId is assigned above; the local `listId` is not yet definitely-assigned as far
    // as the compiler is concerned.
    const quality = result.listId
      ? await getEibAudienceQuality(result.listId)
      : null;
    result.audienceQuality = quality;
    steps.push({
      key: "audience_freeze",
      label: `Audience frozen for this event${
        frozenCount !== null
          ? ` — ${frozenCount.toLocaleString()} contacts`
          : ""
      }${audienceFreezeReachSuffix(frozenCount, quality)}`,
      status: "ok",
      ids: { snapshot_list_id: listId! },
    });
  } catch (e) {
    return fail("audience_freeze", "Freezing the audience", e);
  }

  // 6) Campaign — skipped if this retry already created one.
  let campaignId: UUID;
  try {
    onProgress?.("Creating the campaign and invitation…");
    if (result.campaignId) {
      campaignId = result.campaignId;
      steps.push({
        key: "campaign",
        label: "Campaign already created by the previous attempt",
        status: "ok",
        ids: { campaign_id: campaignId },
      });
    } else {
      const campaign = await createCampaign({
        name: `${plan.eventTitle} — Event in a Box${waveNumber > 1 ? ` (wave ${waveNumber})` : ""}`,
        description: `Auto-created by Event in a Box (${
          plan.mode === "show_up" ? "show-up" : "pre-register"
        } workflow).`,
        event_id: plan.eventId,
      });
      campaignId = campaign.id;
      result.campaignId = campaignId;
      await recordArtifact("campaign_id", campaignId);
      steps.push({
        key: "campaign",
        label: "Campaign created",
        status: "ok",
        ids: { campaign_id: campaignId },
      });
    }
  } catch (e) {
    return fail("campaign", "Campaign", e);
  }

  // 7) Invitation flight. A retry ADOPTS the flight the previous attempt made:
  //    the launch record may be missing its id (lost response on the artifact
  //    call), but the EIB campaign holds exactly one invitation flight, so the
  //    campaign is the durable link that prevents a duplicate.
  let flightId: UUID;
  try {
    if (!result.flightId) {
      result.flightId = (await getLaunchFlight(launchId!)) ?? undefined;
    }
    if (result.flightId) {
      flightId = result.flightId;
      // An adopted flight carries the template resolved by the PREVIOUS attempt. If the resolver's answer has
      // changed since (insurance-state rollout mid-setup, or the venue state was edited), commit refuses with
      // EIB_TEMPLATE_MISMATCH forever. Re-resolve on the server and refresh the never-sent draft in place
      // (migration 392); attest whatever the server settled on. Refuses loudly if the flight was ever touched
      // by a sender.
      const refreshed = await rpc<{ changed: boolean; template_id: string }>(
        "eib_refresh_invitation_template",
        { p_launch_id: launchId, p_token: token, p_flight_id: flightId },
      );
      if (refreshed?.template_id) {
        templateId = refreshed.template_id;
      }
      await recordArtifact("flight_id", flightId).catch(() => undefined);
      steps.push({
        key: "flight",
        label: "Invitation flight already created by the previous attempt",
        status: "ok",
        ids: { flight_id: flightId },
      });
    } else {
      const flight = await createFlight({
        campaign_id: campaignId,
        name: `${plan.eventTitle} — Invitation${waveNumber > 1 ? ` (wave ${waveNumber})` : ""}`,
        list_id: listId!,
        email_subject: plan.flightSubject,
        email_template_id: templateId,
        // The award copy lives in the reserved `static_merge` namespace so the
        // send worker delivers it verbatim to SendGrid (applied last, so no
        // field mapping can override the commit-validated contract). Storing it
        // as top-level keys — as before — meant it never reached SendGrid.
        email_template_data: { static_merge: mergeData },
        generate_vouchers: false,
        // Flight identity: this is the launch's INVITATION (role='invite').
        // Reminders (invite_reminder + offset) are added by Slice 2.
        eib_flight_role: "invite",
        // Identity, not convention: this is what makes the flight adoptable on
        // retry and what the database guard keys on when refusing a direct
        // schedule outside eib_schedule_and_commit.
        eib_launch_id: launchId,
      });
      flightId = flight.id;
      result.flightId = flightId;
      await recordArtifact("flight_id", flightId);
      steps.push({
        key: "flight",
        label: "Invitation flight created",
        status: "ok",
        ids: { flight_id: flightId },
      });
    }
  } catch (e) {
    return fail("flight", "Invitation flight", e);
  }

  // 8) E3: schedule LAST — and schedule + commit in ONE server transaction.
  //    Two calls would leave a window where the invitation is scheduled but no
  //    committed guard exists, so the next wizard session (new key) could
  //    schedule a second one. A thrown error here still does not prove failure:
  //    re-read the launch, because a lost response on a committed transaction
  //    is indistinguishable from one that never ran.
  const finalResult = {
    mode: plan.mode,
    wave_number: waveNumber,
    audience_count: frozenCount,
    flight_id: flightId,
    campaign_id: campaignId,
    list_id: listId!,
    landing_page_id: result.landingPageId ?? null,
  };
  try {
    // Verify the design against SendGrid NOW: the attestation is short-lived
    // and single-use, so it cannot be stockpiled and cannot miss an
    // active-version replacement that happened since the launch began.
    onProgress?.("Verifying the invitation design…");
    const version = await attestSendgridTemplate(launchId!, templateId);
    steps.push({
      key: "blueprint",
      label: `Invitation design verified in SendGrid (active version ${version.slice(0, 8)})`,
      status: "ok",
    });
    await rpc<Record<string, unknown>>("eib_schedule_and_commit", {
      p_launch_id: launchId,
      p_flight_id: flightId,
      p_send_at: effectiveSendAt,
      p_result: finalResult,
      p_token: token,
    });
    steps.push({
      key: "schedule",
      label: `Invitation scheduled for ${new Date(effectiveSendAt).toLocaleString()}`,
      status: cadenceWindowWarning ? "warning" : "ok",
      detail: cadenceWindowWarning ?? undefined,
    });
  } catch (e) {
    let committed = false;
    try {
      const { data } = await supabase
        .from("event_in_a_box_launches")
        .select("status")
        .eq("id", launchId)
        .maybeSingle();
      committed = (data as { status?: string } | null)?.status === "committed";
    } catch {
      committed = false;
    }
    if (!committed) {
      return fail("schedule", "Scheduling the invitation", e);
    }
    steps.push({
      key: "schedule",
      label: `Invitation is scheduled for ${new Date(effectiveSendAt).toLocaleString()} — the confirmation was lost in transit, so this was re-checked against the launch record.`,
      status: cadenceWindowWarning ? "warning" : "ok",
      detail: cadenceWindowWarning ?? undefined,
    });
  }

  await retireSupersededEibDrafts(plan.eventId);
  result.ok = true;
  return result;
}

/**
 * Derive wave B's idempotency key from wave A's — a DETERMINISTIC, distinct,
 * still-valid UUID (flip the first byte). Deterministic so a retry reconciles to
 * the same additional wave (no duplicate win-back blast); a valid UUID because
 * eib_begin_launch.p_idempotency_key is uuid-typed. Flipping any byte guarantees
 * it differs from the base; random-UUID collisions across events are negligible.
 */
export function deriveFormerCancelledKey(base: string): string {
  const hex = base.replace(/-/g, "");
  if (!/^[0-9a-fA-F]{32}$/.test(hex)) {
    // Non-canonical base (should never happen — the wizard uses crypto.randomUUID).
    // Fall back to a fresh UUID so we never emit an invalid one; retry-idempotence
    // is only lost in this impossible branch.
    return typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : base;
  }
  const flipped =
    ((parseInt(hex.slice(0, 2), 16) ^ 0xff) & 0xff)
      .toString(16)
      .padStart(2, "0") + hex.slice(2);
  return `${flipped.slice(0, 8)}-${flipped.slice(8, 12)}-${flipped.slice(12, 16)}-${flipped.slice(16, 20)}-${flipped.slice(20)}`;
}

/**
 * #8 Scope-A: server-authoritative preflight of BOTH waves' invitation cadences
 * before either wave commits.
 *
 * Returns a VALIDATED preflight object, or `null` meaning "could not determine"
 * (RPC error, missing/malformed response). The caller FAILS CLOSED on null for an
 * auto-split launch — it refuses rather than committing wave A into an
 * unverified window (audit MEDIUM: failing open reopened the pre-commit
 * guarantee). Only a well-formed `{applicable:false}` (event type has no
 * auto-cadence) legitimately bypasses the gate.
 */
export interface EibDualWavePreflight {
  applicable: boolean;
  current_sendable?: boolean;
  former_sendable?: boolean;
  current_send_at?: string | null;
  former_send_at?: string | null;
  current_offset_days?: number;
  former_offset_days?: number;
}

async function preflightDualWaveCadence(
  eventId: string,
): Promise<EibDualWavePreflight | null> {
  try {
    const { data, error } = await supabase.rpc(
      "eib_preflight_dual_wave_cadence",
      { p_event_id: eventId },
    );
    if (error || !data || typeof data !== "object") {
      return null;
    }
    const pf = data as Record<string, unknown>;
    // Shape validation — a malformed response must FAIL CLOSED, not be trusted.
    if (typeof pf.applicable !== "boolean") {
      return null;
    }
    // When the cadence applies, both sendable flags MUST be present booleans;
    // otherwise we cannot make the pre-commit decision → fail closed.
    if (
      pf.applicable === true &&
      (typeof pf.current_sendable !== "boolean" ||
        typeof pf.former_sendable !== "boolean")
    ) {
      return null;
    }
    return data as EibDualWavePreflight;
  } catch {
    return null;
  }
}

/**
 * #8 auto-dual-wave. In ONE operator action, a proximity "members" audience is
 * split by the SYSTEM into two independent invitation waves so the operator never
 * has to know who is a current vs a lapsed member:
 *   • Wave A — current (active) members, on the shipped cadence.
 *   • Wave B — former/cancelled members, on the win-back cadence (+ invite resend),
 *              launched as an ADDITIONAL wave with its own idempotency key.
 * The waves run SEQUENTIALLY: A must commit before B begins (only one in-progress
 * launch per event, and B is an additional wave). Both carry the SAME award
 * contract, so B passes the additional-wave award lock. Any audience that is NOT
 * a proximity "members" scope (existing list, active-only, everyone, guest, etc.)
 * is a single wave, unchanged.
 *
 * Before committing anything, a server-authoritative preflight checks BOTH waves'
 * invitation windows (#8 Scope-A). A missed nominal cadence window is now
 * non-fatal while the event remains safely in the future: each wave's apply RPC
 * moves its primary invite to the next safe send time and reports a warning.
 * Events that have started or begin inside the safety buffer remain refused.
 *
 * Partial-failure is safe-by-direction: if A succeeds and B fails for some other
 * reason, A's invitations ARE scheduled (a normal committed launch) and the
 * result carries partialSuccess=true so the wizard reports current invitations as
 * sent and routes recovery to an in-session retry (same key) rather than a
 * fresh-key relaunch that would re-blast wave A.
 */
/**
 * Runtime toggle (system_config.eib_range_night_split_mode) that decides whether a
 * "members" proximity launch uses the #8 dual-wave split. 'split' → restore #8
 * (current + former/cancelled waves with differentiated cadences); anything else
 * (default 'unified') → a SINGLE "members" wave (current+former share one cadence).
 * Flip the key to restore #8 — no code deploy. Missing/unreadable safely → single wave.
 */
async function membersDualWaveSplitEnabled(): Promise<boolean> {
  try {
    const { data } = await supabase
      .from("system_config")
      .select("value")
      .eq("key", "eib_range_night_split_mode")
      .maybeSingle();
    return (
      (data?.value as string | undefined)?.trim().toLowerCase() === "split"
    );
  } catch {
    return false; // fail safe to the single-wave default
  }
}

export async function launchEventInABoxAuto(
  plan: EibPlan,
  onProgress?: (msg: string) => void,
): Promise<EibLaunchResult> {
  // A "members" proximity audience CAN dual-wave-split (current + former/cancelled),
  // but only when the runtime config asks for #8. Otherwise it launches as ONE
  // "members" wave (the members scope already covers current OR former). The
  // dual-wave machinery below is fully retained for a future prospects split.
  const autoSplit =
    plan.audience.kind === "proximity" &&
    plan.audience.scope === "members" &&
    (await membersDualWaveSplitEnabled());
  if (!autoSplit) {
    return launchEventInABox(plan, onProgress);
  }
  // autoSplit guarantees a proximity audience; narrow it so scope-overrides typecheck.
  const baseProx = plan.audience as EibAudienceProximity;

  // #8 Scope-A — preflight BOTH waves' cadences BEFORE committing either, so we
  // never commit wave A into a partial state where wave B's window has already
  // passed (the plausible money-bad case: an event set 7–14 days out, where the
  // current-member 7-day invite is still valid but the former-member 14-day
  // win-back invite is already in the past).
  // A missed nominal window is sendable when the event remains beyond the
  // server's safety buffer: eib_apply_type_cadence moves the PRIMARY invite to
  // a near-term safe time and reports an operator warning; already-past reminder
  // or resend siblings remain stale/skipped. A false sendable flag now means the
  // event itself is too close/started, so nothing commits.
  // FAIL CLOSED: a null preflight means we could not verify the windows (RPC
  // error/malformed). We must NOT commit wave A unverified — refuse and let the
  // operator retry. Only a well-formed {applicable:false} bypasses the gate.
  let runFormerWave = true;
  const preflight = await preflightDualWaveCadence(plan.eventId);
  if (!preflight) {
    return {
      ok: false,
      failedAt: "schedule",
      steps: [
        {
          key: "schedule",
          label:
            "Couldn't verify the invitation send windows just now — nothing was sent. Please try launching again.",
          status: "failed",
        },
      ],
    };
  }
  if (preflight.applicable) {
    if (preflight.current_sendable === false) {
      return {
        ok: false,
        failedAt: "schedule",
        steps: [
          {
            key: "schedule",
            label:
              "This event has started or begins too soon to schedule its invitation safely. Set the event date further out and try again.",
            status: "failed",
          },
        ],
      };
    }
    if (preflight.former_sendable === false) {
      runFormerWave = false;
      onProgress?.(
        "Former/cancelled win-back wave skipped — the event begins too soon to schedule it safely.",
      );
    }
  }

  // Wave A — CURRENT (active) members, shipped cadence.
  onProgress?.("Wave 1 of 2 — current members…");
  const planA: EibPlan = {
    ...plan,
    audienceKind: "current",
    audience: { ...baseProx, scope: "active_members" },
  };
  const resA = await launchEventInABox(planA, (m) =>
    onProgress?.(`Wave 1 (current members): ${m}`),
  );
  const prefixA = (r: EibLaunchResult): EibStepResult[] =>
    r.steps.map((s) => ({ ...s, label: `Wave 1 (current): ${s.label}` }));
  if (!resA.ok) {
    // Wave A failed → its launch released/rolled back its own slot; do NOT start
    // wave B. Surface A's failure directly (labelled).
    return { ...resA, steps: prefixA(resA) };
  }

  if (!runFormerWave) {
    // Preflight decided the former/cancelled win-back window is already past.
    // Wave A committed as a normal single-wave launch; report the deliberate skip
    // (NOT a failure — there is nothing to retry).
    return {
      ...resA,
      steps: [
        ...prefixA(resA),
        {
          key: "schedule",
          label:
            "Wave 2 (former/cancelled): SKIPPED — the event begins too soon to schedule this wave safely, so only current members were invited.",
          status: "skipped",
        },
      ],
    };
  }

  // Wave B — FORMER/CANCELLED members, win-back cadence + resend, ADDITIONAL wave.
  onProgress?.("Wave 2 of 2 — former / cancelled members…");
  const planB: EibPlan = {
    ...plan,
    audienceKind: "former_cancelled",
    audience: { ...baseProx, scope: "former_cancelled_members" },
    allowAdditionalWave: true,
    // DETERMINISTIC key derived from wave A's — distinct from A, stable across
    // retries (the wizard holds plan.idempotencyKey in state). A lost-response
    // commit + operator retry then RECONCILES to the same additional wave instead
    // of blasting a SECOND win-back invitation. (A fresh random key here defeats
    // idempotency — audit HIGH.) It MUST stay a valid UUID: eib_begin_launch's
    // p_idempotency_key is uuid-typed, so a '-fc' suffix would fail the cast —
    // deriveFormerCancelledKey flips the first byte instead.
    idempotencyKey: deriveFormerCancelledKey(plan.idempotencyKey),
  };
  const resB = await launchEventInABox(planB, (m) =>
    onProgress?.(`Wave 2 (former/cancelled): ${m}`),
  );
  const prefixB = (r: EibLaunchResult): EibStepResult[] =>
    r.steps.map((s) => ({
      ...s,
      label: `Wave 2 (former/cancelled): ${s.label}`,
    }));

  const partial = resA.ok && !resB.ok;
  return {
    ok: resA.ok && resB.ok,
    // Wave A committed but wave B failed → PARTIAL, not "nothing sent". The
    // wizard must report current invitations as scheduled and route recovery to
    // an in-session retry (same key ⇒ wave A reconciles idempotently, wave B
    // completes) rather than a fresh-key relaunch.
    partialSuccess: partial,
    // Wave B's OWN failure marker + size, so the wizard can react to a wave-B
    // server decision (e.g. audience-size confirmation) truthfully — wave A is
    // already scheduled; only the FORMER wave needs handling.
    partialWaveB: partial
      ? {
          detail: resB.steps.find((s) => s.status === "failed")?.detail ?? "",
          audienceCount: resB.audienceCount ?? null,
        }
      : undefined,
    steps: [...prefixA(resA), ...prefixB(resB)],
    campaignId: resA.campaignId,
    flightId: resA.flightId,
    listId: resA.listId,
    landingPageId: resA.landingPageId,
    audienceCount: (resA.audienceCount ?? 0) + (resB.audienceCount ?? 0),
  };
}

/** Preview-step audience size. `capped` means "at least this many". */
export interface EibAudienceEstimate {
  count: number;
  capped: boolean;
}

export async function estimateAudience(
  audience: EibAudience,
): Promise<EibAudienceEstimate | null> {
  try {
    if (audience.kind === "existing") {
      const { data } = await supabase
        .from("lists")
        .select("contact_count")
        .eq("id", audience.listId)
        .maybeSingle();
      const listCount = data?.contact_count as number | null;
      return listCount === null || listCount === undefined
        ? null
        : { count: listCount, capped: false };
    }
    // Proximity: count the criteria directly, so the operator sees the size
    // BEFORE committing to a mass send rather than discovering it at launch.
    // Compiled by the same builder the launch uses, so preview and launch
    // cannot disagree about what the criteria mean. A timeout or an
    // un-evaluable criteria set yields null ("unknown"), and the launch-time
    // gate still applies.
    const { data, error } = await supabase.rpc(
      "eib_estimate_criteria_audience",
      {
        p_criteria: proximityCriteria(audience),
      },
    );
    if (error) {
      return null;
    }
    // The RPC counts through a capped subquery and reports whether it hit the
    // cap, so a very large audience reads as "100,000+" rather than a number
    // that looks exact. Casting this object straight to a number is what made
    // the preview render "[object Object] contacts".
    const body = data as { count?: number; capped?: boolean } | null;
    if (typeof body?.count !== "number") {
      return null;
    }
    return { count: body.count, capped: body.capped === true };
  } catch {
    return null;
  }
}

/**
 * Download an EIB invitation's TARGETED RECIPIENT LIST as CSV — First/Last/Email/Phone plus a
 * Registered (Y/N) column that is current as of export. The recipient set is the frozen snapshot
 * list that received the off-platform SMS (segment or proximity, both freeze the same way);
 * registration status is computed live. Admin / flight-management role only (enforced server-side
 * by the export-eib-recipients edge function). Mirrors downloadDynamicSegmentCsv.
 */
export async function downloadEibRecipientsCsv(
  launchId: string,
): Promise<{ rowCount: number; truncated: boolean; filename: string }> {
  const { data: sessionData, error: sessionError } =
    await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (sessionError || !accessToken) {
    throw sessionError ?? new Error("An authenticated session is required");
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const response = await fetch(
    `${supabaseUrl}/functions/v1/export-eib-recipients`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        apikey: anonKey,
        "Content-Type": "application/json",
        "x-application-name": "uslawshield-crm",
      },
      body: JSON.stringify({ launch_id: launchId }),
    },
  );

  if (!response.ok) {
    let message = `Recipient export failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: string };
      if (body.error) {
        message = body.error;
      }
    } catch {
      // Preserve the status-based message for a non-JSON gateway response.
    }
    throw new Error(message);
  }

  const csv = await response.text();
  const parsedRowCount = Number(response.headers.get("X-Row-Count"));
  const rowCount = Number.isFinite(parsedRowCount) ? parsedRowCount : 0;
  const truncated = response.headers.get("X-Truncated") === "true";
  const filename =
    response.headers.get("X-Filename") ||
    `eib-recipients-${new Date().toISOString().slice(0, 10)}.csv`;

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);

  return { rowCount, truncated, filename };
}
